import React, { useState, useEffect, useRef } from 'react';
import { api } from './App';

const TYPE_OPTIONS = [
  { value: 'bug',     label: "Something's broken" },
  { value: 'feature', label: 'Feature request' },
  { value: 'general', label: 'General feedback' },
];

function SQAvatar() {
  return (
    <div className="fb-sq-avatar" title="SQETrack Support">
      SQ
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="fb-typing">
      <span /><span /><span />
    </div>
  );
}

export default function Feedback({ onViewed }) {
  const [phase, setPhase] = useState('form'); // 'form' | 'conversation' | 'done'
  const [fbType, setFbType] = useState('general');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [currentFbId, setCurrentFbId] = useState(null);
  const [currentText, setCurrentText] = useState('');
  const [triageQuestion, setTriageQuestion] = useState(null); // null = loading, '' = none, string = question
  const [triageLoading, setTriageLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [savingReply, setSavingReply] = useState(false);
  const [history, setHistory] = useState([]);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (onViewed) onViewed();
  }, []); // eslint-disable-line

  useEffect(() => {
    api('/api/feedback').then(data => {
      if (Array.isArray(data)) setHistory(data);
    });
  }, [phase]);

  useEffect(() => {
    if (phase === 'conversation') {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [triageQuestion, phase]);

  const submit = async () => {
    if (!description.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api('/api/feedback', {
        method: 'POST',
        body: JSON.stringify({
          type: fbType,
          description: description.trim(),
          context_route: window.location.pathname,
          context_browser: navigator.userAgent,
        }),
      });
      if (res.error) { setError(res.error); return; }
      setCurrentFbId(res.id);
      setCurrentText(description.trim());
      setTriageQuestion(null); // null = loading
      setTriageLoading(true);
      setPhase('conversation');
      // Call triage async
      try {
        const tr = await api(`/api/feedback/${res.id}/triage`, { method: 'POST' });
        setTriageQuestion(tr.question || '');
      } catch {
        setTriageQuestion('');
      } finally {
        setTriageLoading(false);
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitReply = async () => {
    if (!replyText.trim()) { setPhase('done'); return; }
    setSavingReply(true);
    try {
      await api(`/api/feedback/${currentFbId}/followup`, {
        method: 'POST',
        body: JSON.stringify({ answer: replyText.trim() }),
      });
    } finally {
      setSavingReply(false);
      setPhase('done');
    }
  };

  const reset = () => {
    setPhase('form');
    setFbType('general');
    setDescription('');
    setCurrentFbId(null);
    setCurrentText('');
    setTriageQuestion(null);
    setReplyText('');
    setError(null);
  };

  return (
    <div className="feedback-page">
      <div className="feedback-intro">
        <div className="feedback-title">Help us improve SQETrack</div>
        <p className="feedback-desc">Tell us what you were doing, what happened, and what you expected. Specific details help us fix things faster.</p>
      </div>

      {phase === 'form' && (
        <div className="feedback-form-card">
          <div className="feedback-type-row">
            {TYPE_OPTIONS.map(opt => (
              <label key={opt.value} className={`feedback-type-btn ${fbType === opt.value ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="fb_type"
                  value={opt.value}
                  checked={fbType === opt.value}
                  onChange={() => setFbType(opt.value)}
                  style={{ display: 'none' }}
                />
                {opt.label}
              </label>
            ))}
          </div>
          <textarea
            className="feedback-textarea"
            placeholder="What happened or what would you like to see?"
            value={description}
            onChange={e => setDescription(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(); }}
            rows={5}
          />
          {error && <div className="feedback-error">{error}</div>}
          <div className="feedback-input-row">
            <span style={{ fontSize: 11, color: 'var(--hint)' }}>Ctrl+Enter to submit</span>
            <button
              className="feedback-submit-btn"
              onClick={submit}
              disabled={submitting || !description.trim()}
            >
              {submitting ? 'Sending…' : 'Send feedback'}
            </button>
          </div>
          <p className="feedback-disclosure">SQETrack Support uses AI to help understand your feedback. Every submission is reviewed by a real person.</p>
        </div>
      )}

      {phase === 'conversation' && (
        <div className="feedback-form-card">
          <div className="fb-conv-thread">
            {/* User's message */}
            <div className="fb-conv-row fb-conv-row-user">
              <div className="fb-bubble fb-bubble-user">
                <div className="fb-bubble-meta">
                  <span className="fb-type-tag">{fbType}</span>
                  <span className="fb-date">Just now</span>
                </div>
                <div className="fb-bubble-text">{currentText}</div>
              </div>
            </div>

            {/* Support response */}
            <div className="fb-conv-row fb-conv-row-support">
              <SQAvatar />
              <div className="fb-bubble fb-bubble-admin">
                <div className="fb-bubble-meta">
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)' }}>SQETrack Support</span>
                </div>
                {triageLoading ? (
                  <TypingIndicator />
                ) : triageQuestion ? (
                  <>
                    <div className="fb-bubble-text">{triageQuestion}</div>
                    <div style={{ marginTop: '0.75rem' }}>
                      <textarea
                        className="feedback-textarea"
                        placeholder="Your reply (optional)…"
                        value={replyText}
                        onChange={e => setReplyText(e.target.value)}
                        rows={3}
                        autoFocus
                      />
                      <div className="feedback-input-row" style={{ marginTop: '0.5rem' }}>
                        <button className="feedback-submit-btn btn-ghost" onClick={() => setPhase('done')}>
                          Skip
                        </button>
                        <button className="feedback-submit-btn" onClick={submitReply} disabled={savingReply}>
                          {savingReply ? 'Saving…' : 'Reply'}
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="fb-bubble-text">Thanks. We've logged this for review.</div>
                )}
              </div>
            </div>

            {/* Show done button when no question or after skip */}
            {!triageLoading && !triageQuestion && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                <button className="feedback-submit-btn" onClick={() => setPhase('done')}>Done</button>
              </div>
            )}

            <div ref={bottomRef} />
          </div>
          <p className="feedback-disclosure" style={{ marginTop: '1rem' }}>SQETrack Support uses AI to help understand your feedback. Every submission is reviewed by a real person.</p>
        </div>
      )}

      {phase === 'done' && (
        <div className="feedback-form-card feedback-thanks">
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: '.5rem' }}>Thanks. We read everything.</div>
          <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: '1rem', lineHeight: 1.5 }}>
            Your feedback has been sent. If you reported a bug, we'll look into it.
          </p>
          <button className="feedback-submit-btn" onClick={reset}>Send more feedback</button>
        </div>
      )}

      {history.length > 0 && (
        <div className="feedback-history">
          <div className="feedback-section-title" style={{ marginBottom: '0.75rem' }}>Your feedback history</div>
          {history.map(f => (
            <div key={f.id} className="fb-thread">
              <div className="fb-bubble fb-bubble-user">
                <div className="fb-bubble-meta">
                  <span className="fb-type-tag">{f.type || 'general'}</span>
                  {f.resolved && <span className="fb-resolved-tag">✓ Resolved</span>}
                  <span className="fb-date">{f.created_at?.slice(0, 10)}</span>
                </div>
                <div className="fb-bubble-text">{f.description || f.text}</div>
                {f.followup_question && f.followup_answer && (
                  <div className="fb-followup">
                    <span className="fb-followup-q">{f.followup_question}</span>
                    <span className="fb-followup-a">{f.followup_answer}</span>
                  </div>
                )}
              </div>
              {f.admin_reply && (
                <div className="fb-conv-row fb-conv-row-support">
                  <SQAvatar />
                  <div className="fb-bubble fb-bubble-admin">
                    <div className="fb-bubble-meta">
                      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)' }}>SQETrack team</span>
                      {f.admin_replied_at && <span className="fb-date">{f.admin_replied_at.slice(0, 10)}</span>}
                    </div>
                    <div className="fb-bubble-text">{f.admin_reply}</div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
